import { SignalType } from 'gabi-api-ts/v2/common/signals/signals';
import {ProgramType} from 'gabi-api-ts/v2/hcp/common/hcp_common';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';

import {getHcpProgramConfig} from '@/components/business/analytics/patients/hcp-program-config';
import {PatientsActiveTableRow} from '@/components/business/analytics/patients/patients-active-table-row';
import {InfoMessage} from '@/components/form/info-message';
import {TableSortable} from '@/components/table/table-sortable';
import {TableSortableColumn} from '@/components/table/table-sortable-types';
import ComponentErrorMessage from '@/components/widgets/component-error-message';
import {SignalDefinitions} from '@/model/signal-definitions/signal-definition';
import {PatientTransformed} from '@/model/transformers/transform-patient';
import {formatApiFullDateToJSDate} from '@/services/api-requests/requests-utils';

type PatientsActiveTableProps = {
    patients: PatientTransformed[];
    program: ProgramType;
}

const PatientsActiveTable = ({patients, program}: PatientsActiveTableProps) => {
    const { t } = useTranslation();

    const programConfig = getHcpProgramConfig(program);
    const tableSortableHeaderColumn: TableSortableColumn<PatientTransformed>[] = [
        {
            id: 'id',
            label: '',
            sortable: false,
        },
        {
            id: 'lastName',
            label: t('patient.title') ?? 'Patient',
            compareFn: (a, b) => {
                return a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase());
            },
            sortable: true,
        },
        {
            id: 'lastRecordingDataTimeSortable',
            label: t('patient.lastRecording') ?? 'Last recording',
            compareFn: (a, b) => {
                const aStr = a.lastRecordingDataTime ? formatApiFullDateToJSDate(a.lastRecordingDataTime).toISOString() : '2000-01-01';
                const bStr = b.lastRecordingDataTime ? formatApiFullDateToJSDate(b.lastRecordingDataTime).toISOString() : '2000-01-01';
                return aStr.localeCompare(bStr);
            },
            sortable: true,
        },
    ];

    if (programConfig.news2.spot) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.news2.statistics.spot',
            label: t('signalType.news2') ?? 'Score',
            subLabel: 'Last data point',
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.NEWS2].colors.normal}`
            },
            sortable: false,
        });
    }

    if (programConfig.bloodPressure.spot) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.bloodPressureSys.statistics.spot',
            label: t('signalType.bloodPressureSystolic') ?? 'Blood Pressure - Systolic',
            subLabel: 'Last data point',
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_BP_SYS].colors.normal}`
            },
            sortable: false,
        });
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.bloodPressureDia.statistics.spot',
            label: t('signalType.bloodPressureDiastolic') ?? 'Blood Pressure - Diastolic',
            subLabel: 'Last data point',
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_BP_DIA].colors.normal}`
            },
            sortable: false,
        });
    }

    if (programConfig.pulseRate.spot) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.pulseRate.statistics.spot',
            label: t('signalType.pulseRate') ?? 'Pulse Rate',
            subLabel: 'Last data point',
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_PR].colors.normal}`
            },
            sortable: false,
        });
    }
    if (programConfig.pulseRate.average) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.pulseRate.statistics.average',
            label: t('signalType.pulseRate') ?? 'Pulse Rate',
            subLabel: t('global.average') ?? 'Average',
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_PR].colors.normal}`
            },
            sortable: false,
        });
    }

    if (programConfig.spo2.spot) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.spo2.statistics.spot',
            label: t('signalType.spo2') ?? 'SpO2',
            subLabel: 'Last data point',
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_SPO2].colors.normal}`
            },
            sortable: false,
        });
    }
    if (programConfig.spo2.average) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.spo2.statistics.average',
            label: t('signalType.spo2') ?? 'SpO2',
            subLabel: t('global.average') ?? 'Average',
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_SPO2].colors.normal}`
            },
            sortable: false,
        });
    }

    if (programConfig.respiratoryRate.spot) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.respiratoryRate.statistics.spot',
            label: t('signalType.respiratoryRate') ?? 'SpO2',
            subLabel: 'Last data point',
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_RR].colors.normal}`
            },
            sortable: false,
        });
    }

    if (programConfig.pulseRate.eventsBelow) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.pulseRate.eventsCountLow.eventCount',
            label: t('global.pulseRate') ?? 'Pulse Rate',
            subLabel: `${t('global.eventsTitle') ?? 'Events/h'} - ${t('global.below') ?? 'Below'}`,
            className: 'separatorCell signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_PR].colors.normal}`
            },
            sortable: false,
        });
    }
    if (programConfig.pulseRate.eventsAbove) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.pulseRate.eventsCountHigh.eventCount',
            label: t('global.pulseRate') ?? 'Pulse Rate',
            subLabel: `${t('global.eventsTitle') ?? 'Events/h'} - ${t('global.above') ?? 'Above'}`,
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_PR].colors.normal}`
            },
            sortable: false,
        });
    }
    if (programConfig.spo2.eventsBelow) {
        tableSortableHeaderColumn.push({
            id: 'lastRecordingInformation.spo2.eventPercent.eventPercent',
            label: t('global.spo2') ?? 'SpO2',
            subLabel: `${t('global.percentage') ?? 'Percentage'} - ${t('global.below') ?? 'Below'}`,
            className: 'signalTableHeader',
            headerStyle: {
                borderTop: `5px solid ${SignalDefinitions[SignalType.Signal_SPO2].colors.normal}`
            },
            sortable: false,
        });
    }

    if (program !== null && patients) {
        if (patients && patients.length > 0) {
            return (
                <StyledWrapper>
                    <TableSortable
                        data={patients}
                        keyFn={item => `${item.patientId?.id ?? ''}`}
                        columns={tableSortableHeaderColumn}
                        defaultSorting={{id: 'lastRecordingDataTimeSortable', sortByOrder: 'desc'}}
                        TableBodyRowComponent={(props) => <PatientsActiveTableRow {...props} programConfig={programConfig} />}
                    />
                </StyledWrapper>
            );
        }
        else {
            return <InfoMessage infoMessage="No active patients." />;
        }
    }
    else {
        return <ComponentErrorMessage component="PatientsActiveTable" />;
    }
};

//language=SCSS
const StyledWrapper = styled.div`
& {
    thead > tr {
        > th:nth-child(1 of .signalTableHeader) {
            border-left-width: 5px;
        }
    }
}
`;

export {PatientsActiveTable};
